import { Select as SelectPrimitive, type SelectRootItemComponentProps } from "@kobalte/core/select";
import { Check, ChevronDown, Plus } from "@fleet/icons";
import { type JSX, Show } from "solid-js";
import { cn } from "@tendly/shared";

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * CTA rendered inside the dropdown when the Select has no options (AWS-style:
 * an empty combobox offers "Create X" / "Go to X" instead of a dead control).
 *
 * Pass `onSelect` to run an action (open a creation dialog) or `href` to render
 * a link (navigate to the screen that provides the options). Provide exactly one.
 * `can` (default `true`) gates the CTA on permission — when `false` the empty
 * dropdown falls back to a plain "no options" message and no creation affordance
 * is shown. Omit `emptyAction` entirely when the source is not creatable.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Permission gate. When false, the CTA is hidden. Defaults to true. */
  can?: boolean;
  /** Leading icon for the CTA. Defaults to a plus glyph. */
  icon?: JSX.Element;
  /** Optional helper line shown above the CTA (e.g. "No reminders yet"). */
  description?: string;
}

interface SelectProps {
  /** Options to display. */
  options: SelectOption[];
  /** Selected value (controlled). */
  value: string;
  /** Value change. Receives the selected option's `value`. */
  onChange: (value: string) => void;
  /** Text when there is no selection. */
  placeholder?: string;
  /** Accessible label for the control. */
  "aria-label"?: string;
  disabled?: boolean;
  class?: string;
  /**
   * Shown inside the dropdown when `options` is empty. Lets the user jump to the
   * place that creates options instead of facing a dead Select. See
   * {@link SelectEmptyAction}.
   */
  emptyAction?: SelectEmptyAction;
  /** Message shown in the empty dropdown when there is no (permitted) CTA. */
  emptyLabel?: string;
}

/** Listbox item — extracted at module level (not redefined during render). */
function SelectItem(itemProps: SelectRootItemComponentProps<SelectOption>): JSX.Element {
  return (
    <SelectPrimitive.Item
      item={itemProps.item}
      class={cn(
        "relative flex cursor-pointer select-none items-center justify-between gap-2 rounded-md px-3 py-2 text-sm outline-none",
        "text-popover-foreground transition-colors",
        "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      )}
    >
      <SelectPrimitive.ItemLabel>{itemProps.item.rawValue.label}</SelectPrimitive.ItemLabel>
      <SelectPrimitive.ItemIndicator>
        <Check class="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

/**
 * Premium Select on top of `@kobalte/core` — theme-safe replacement for the native
 * `<select>`. Keyboard/typeahead/ARIA come from Kobalte; styling and the animated panel
 * follow the pattern of `popover.tsx`/`dialog.tsx` with the repo's tokens.
 */
export function Select(props: SelectProps): JSX.Element {
  const selectedOption = () => props.options.find((o) => o.value === props.value) ?? null;
  const isEmpty = () => props.options.length === 0;

  return (
    <SelectPrimitive<SelectOption>
      options={props.options}
      optionValue="value"
      optionTextValue="label"
      optionDisabled="disabled"
      value={selectedOption()}
      onChange={(option) => {
        if (option) {
          props.onChange(option.value);
        }
      }}
      disallowEmptySelection
      disabled={props.disabled}
      placeholder={props.placeholder}
      itemComponent={SelectItem}
    >
      <SelectPrimitive.Trigger
        aria-label={props["aria-label"]}
        class={cn(
          "flex w-full items-center justify-between gap-2 rounded-md border border-border/60 bg-transparent px-3 py-2 text-sm",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "data-[expanded]:ring-2 data-[expanded]:ring-accent/40",
          props.class,
        )}
      >
        <SelectPrimitive.Value<SelectOption> class="truncate text-left">
          {(state) => (
            <Show
              when={state.selectedOption()}
              fallback={<span class="text-muted-foreground">{props.placeholder}</span>}
            >
              {state.selectedOption()?.label}
            </Show>
          )}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon class="shrink-0 text-muted-foreground transition-transform duration-200 data-[expanded]:rotate-180">
          <ChevronDown class="h-4 w-4" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          class={cn(
            "ui-select-content z-[260] overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md outline-none",
          )}
        >
          <Show
            when={!isEmpty()}
            fallback={<SelectEmptyContent action={props.emptyAction} label={props.emptyLabel} />}
          >
            <SelectPrimitive.Listbox class="max-h-60 overflow-y-auto p-1" />
          </Show>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive>
  );
}

/**
 * Content shown inside an empty Select dropdown. Renders the `emptyAction` CTA
 * (creation button or navigation link) when present and permitted, otherwise a
 * muted "no options" message. The CTA closes the dropdown on activation.
 *
 * The CTA is keyboard-reachable (it is a native button/anchor, auto-focusable
 * inside the portalled panel) and inherits the panel's opacity-only entrance, so
 * it respects `prefers-reduced-motion` via the global guard like the rest of the UI.
 */
export function SelectEmptyContent(props: { action?: SelectEmptyAction; label?: string }) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm font-medium outline-none",
    "text-popover-foreground transition-colors cursor-pointer",
    "hover:bg-accent hover:text-accent-foreground",
    "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/40",
  );

  const ctaIcon = () => props.action?.icon ?? <Plus class="h-4 w-4 shrink-0 text-accent" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-3 py-3 text-center text-sm text-muted-foreground">
          {/* Copy lives at the call site so this primitive stays i18n-clean; the
              neutral em-dash is the last-resort placeholder when no label is given. */}
          {props.label ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1 p-1">
          <Show when={action().description}>
            <p class="px-3 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}
